import { Component, inject } from '@angular/core';
import { Validators, FormBuilder, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { shareReplay, combineLatest, map, tap, forkJoin, switchMap, take } from 'rxjs';
import Users from '../../core/services/users.service';
import { AuthService } from '../../core/services/auth.service';
import { ProfileService } from '../../core/services/profile.service';
import { AsyncPipe } from '@angular/common';
import { passwordMatchValidator } from '../../shared/components/validators/password-match.validator';
import { Profile } from '../../shared/models/users-profile.models';
import { UserEdit } from '../../shared/models/users.models';
import { StatusModalService } from '../../core/services/status-modal.service';
import {
  PasswordStrengthError,
  passwordStrengthValidator,
} from '../../shared/components/validators/password-strenght.validator';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputField } from '../../shared/components/input-field/input-field';

@Component({
  selector: 'app-user-profile',
  imports: [
    AsyncPipe,
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    InputField,
    MatButtonModule,
    MatDividerModule,
    ReactiveFormsModule,
    FontAwesomeModule,
  ],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent {
  private readonly usersService = inject(Users);

  private readonly profileService = inject(ProfileService);

  private readonly authService = inject(AuthService);

  private readonly formBuilder = inject(FormBuilder);

  private readonly statusModalService = inject(StatusModalService);

  private readonly currentUserId = this.authService.getUser()?.id || '';

  public readonly userProfileSettingsForm = this.formBuilder.group(
    {
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: [{ value: '', disabled: true }],
      username: [{ value: '', disabled: true }],
      role: [{ value: '', disabled: true }],
      newPassword: ['', [passwordStrengthValidator()]],
      confirmPassword: [''],
    },
    {
      validators: passwordMatchValidator(),
    },
  );

  public readonly currentUser$ = this.usersService.getUser(this.currentUserId).pipe(shareReplay(1));

  public readonly currentUserProfile$ = this.profileService
    .getBaseProfileDataByUserId(this.currentUserId)
    .pipe(shareReplay(1));

  public readonly userSettingsData$ = combineLatest([
    this.currentUser$,
    this.currentUserProfile$,
  ]).pipe(
    map(([user, profile]) => ({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: user.email,
      username: user.username,
      role: user.role,
      roleId: user.role._id,
      department: user.department._id,
    })),
    tap((userSettingsData) => {
      this.userProfileSettingsForm.patchValue({
        firstName: userSettingsData.firstName,
        lastName: userSettingsData.lastName,
        email: userSettingsData.email,
        username: userSettingsData.username,
        role: userSettingsData.role?.name ?? '',
      });
    }),
    shareReplay(1),
  );

  private showSuccessModal(passwordChanged: boolean): void {
    this.statusModalService.open({
      state: 'success',
      title: 'Profile Updated',
      message: passwordChanged
        ? 'Your profile details and password were updated successfully.'
        : 'Your profile details were updated successfully.',
      highlights: passwordChanged ? ['profile details', 'password'] : undefined,
    });
  }

  private showErrorModal(): void {
    this.statusModalService.open({
      state: 'error',
      title: 'Error Occurred',
      message: 'Something went wrong while updating your profile settings.',
      highlights: ['profile settings'],
    });
  }

  public handleSaveChanges(): void {
    if (this.userProfileSettingsForm.invalid) {
      this.userProfileSettingsForm.markAllAsTouched();
      return;
    }

    const formValue = this.userProfileSettingsForm.getRawValue();

    const shouldChangePassword = !!formValue.newPassword && !!formValue.confirmPassword;

    this.userSettingsData$
      .pipe(
        take(1),
        switchMap((userSettingsData) => {
          const updateProfileRequest$ = this.profileService.updateProfile(this.currentUserId, {
            userId: this.currentUserId,
            personalDetails: {
              firstName: formValue.firstName ?? '',
              lastName: formValue.lastName ?? '',
              fullName: `${formValue.firstName ?? ''} ${formValue.lastName ?? ''}`.trim(),
            },
            companyDetails: {},
            contacts: {},
          } as Partial<Profile>);

          if (!shouldChangePassword) {
            return updateProfileRequest$;
          }

          const updatePasswordPayload: UserEdit = {
            username: userSettingsData.username,
            email: userSettingsData.email,
            roleId: userSettingsData.roleId,
            department: userSettingsData.department,
            password: formValue.newPassword ?? '',
            passwordConf: formValue.confirmPassword ?? '',
            recreate: false,
            unlock: false,
          };

          const updatePasswordRequest$ = this.usersService.editUser(
            this.currentUserId,
            updatePasswordPayload,
          );

          return forkJoin([updateProfileRequest$, updatePasswordRequest$]);
        }),
      )
      .subscribe({
        next: () => {
          this.userProfileSettingsForm.patchValue({
            newPassword: '',
            confirmPassword: '',
          });

          this.userProfileSettingsForm.markAsPristine();
          this.userProfileSettingsForm.markAsUntouched();

          this.showSuccessModal(shouldChangePassword);
        },
        error: () => {
          this.showErrorModal();
        },
      });
  }

  public getPasswordErrorMessage(control: AbstractControl | null): string {
    const passwordStrengthError = control?.getError(
      'passwordStrength',
    ) as PasswordStrengthError | null;

    if (!passwordStrengthError) {
      return '';
    }

    if (!passwordStrengthError.hasMinLength) {
      return 'Password must be at least 10 characters long';
    }

    const missingCriteria: string[] = [];

    if (!passwordStrengthError.hasUppercase) {
      missingCriteria.push('At least 1 uppercase character');
    }

    if (!passwordStrengthError.hasNumber) {
      missingCriteria.push('At least 1 numeric character');
    }

    if (!passwordStrengthError.hasSpecialCharacter) {
      missingCriteria.push('At least 1 special character.(.,!@#$%^&*?(){}[]|_<>)');
    }

    return `Password does not match the following criteria: ${missingCriteria.join('. ')}`;
  }
}
